import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import RedactedValue from '../components/RedactedValue';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import JobApplicationModal from '../components/jobs/JobApplicationModal';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  UsersIcon,
  EyeIcon,
  BookmarkIcon,
  CheckCircleIcon,
  SparklesIcon,
  ClockIcon,
  BellIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

interface Job {
  id: string;
  title: string;
  description: string;
  requirements: string[];
  skillsRequired: string[];
  employerId: string;
  employerName: string;
  companyDescription?: string;
  location: string;
  remoteType: 'onsite' | 'remote' | 'hybrid';
  jobType: 'full-time' | 'part-time' | 'contract' | 'internship';
  experienceLevel: 'entry' | 'junior' | 'mid' | 'senior' | 'lead';
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  benefits?: string[];
  applicationDeadline?: string;
  applicationsCount?: number;
  /** Set by the API when the count was withheld (signed-out viewer). */
  applicationsCountLocked?: boolean;
  viewsCount: number;
  tags: string[];
  category: string;
  industryCategory: string;
  competitionLevel: 'low' | 'medium' | 'high';
  isFeatured: boolean;
  
  // Auto-offer feature fields
  autoOffer?: {
    enabled: boolean;
    timeLimit: number;
    requiredCourses: Array<{
      courseId: string;
      courseName: string;
      minimumGrade: number;
    }>;
    minimumOverallScore?: number;
    createdAt: Date;
  };
  autoOfferDeadline?: Date | string;
  autoOfferEnabled?: boolean;
  autoOfferTimeLimit?: number;
  autoOfferRequiredCourses?: Array<{
    courseId: string;
    courseName: string;
    minimumGrade: number;
  }>;
  autoOfferMinimumScore?: number;
  createdAt: string;
}

interface JobMatch {
  overallMatchScore: number;
  skillMatchScore: number;
  courseMatchScore: number;
  experienceMatchScore: number;
  assessmentMatchScore: number;
  matchedSkills: string[];
  missingSkills: string[];
  recommendedCourses: string[];
  qualificationLevel: 'under-qualified' | 'qualified' | 'well-qualified' | 'over-qualified';
  improvementSuggestions: string[];
}

interface JobSearchFilters {
  keywords?: string;
  location?: string;
  remoteType?: string[];
  jobType?: string[];
  experienceLevel?: string[];
  salaryMin?: number;
  salaryMax?: number;
  category?: string;
  industryCategory?: string;
  minimumMatchScore?: number;
  postedWithin?: number;
  sortBy?: 'relevance' | 'salary' | 'posted_date' | 'match_score' | 'competition';
  searchPriority?: 'title' | 'employer' | 'category' | 'skills' | 'courses';
  searchStrategy?: 'prioritized' | 'standard';
}

// Function to clean HTML content and return plain text (for job listings)
const cleanHTMLToText = (htmlString: string, maxLength: number = 150) => {
  // Strip HTML tags and clean up whitespace
  const textOnly = htmlString.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  
  // Truncate if needed
  if (textOnly.length > maxLength) {
    return textOnly.substring(0, maxLength) + '...';
  }
  
  return textOnly;
};

const JobsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [matches, setMatches] = useState<Record<string, JobMatch>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false); // Start false until we know there are more
  const [page, setPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<JobSearchFilters>({
    sortBy: 'match_score'
  });
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  const observerRef = useRef<HTMLDivElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter options
  const remoteTypeOptions = [
    { value: 'onsite', label: 'On-site' },
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' }
  ];

  const jobTypeOptions = [
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' }
  ];

  const experienceLevelOptions = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'lead', label: 'Lead/Principal' }
  ];

  const sortOptions = [
    { value: 'match_score', label: 'Best Match' },
    { value: 'salary', label: 'Highest Salary' },
    { value: 'posted_date', label: 'Most Recent' },
    { value: 'competition', label: 'Least Competition' },
    { value: 'relevance', label: 'Most Relevant' }
  ];

  const postedWithinOptions = [
    { value: 1, label: 'Last 24 hours' },
    { value: 7, label: 'Last week' },
    { value: 30, label: 'Last month' },
    { value: 90, label: 'Last 3 months' }
  ];

  // Extract search query from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchInput(searchParam);
      setFilters(prev => ({ ...prev, keywords: searchParam }));
    }
  }, [location.search]);

  // Parse and prioritize search terms
  const parseSearchTerms = (searchTerm: string) => {
    if (!searchTerm.trim()) return { keywords: searchTerm };

    // Split search terms and analyze them
    const terms = searchTerm.toLowerCase().split(/\s+/).filter(term => term.length > 0);
    
    // Common job titles keywords that should get title priority
    const titleKeywords = ['developer', 'engineer', 'manager', 'analyst', 'designer', 'architect', 'lead', 'senior', 'junior', 'principal', 'staff', 'director', 'specialist', 'consultant', 'coordinator', 'administrator', 'technician', 'intern'];
    
    // Common company/employer indicators - EXPANDED LIST
    const employerKeywords = ['inc', 'corp', 'llc', 'ltd', 'company', 'corporation', 'enterprises', 'solutions', 'technologies', 'systems', 'group', 'associates', 'investments', 'capital', 'financial', 'bank', 'partners', 'holdings', 'ventures', 'advisors', 'consulting', 'services', 'industries', 'international', 'global', 'worldwide', 'communications', 'media', 'networks', 'labs', 'research', 'development', 'innovation'];
    
    // Technology/skills keywords
    const skillKeywords = ['javascript', 'python', 'java', 'react', 'angular', 'vue', 'node', 'express', 'django', 'flask', 'sql', 'nosql', 'mongodb', 'postgresql', 'mysql', 'aws', 'azure', 'docker', 'kubernetes', 'git', 'html', 'css', 'typescript', 'php', 'ruby', 'go', 'rust', 'swift', 'kotlin', 'scala', 'machine learning', 'ai', 'data science', 'devops', 'agile', 'scrum'];

    let priority: 'title' | 'employer' | 'category' | 'skills' | 'courses' = 'employer'; // default to employer for better company search
    
    // Determine search priority based on terms
    const hasTitle = terms.some(term => titleKeywords.includes(term));
    const hasEmployer = terms.some(term => employerKeywords.includes(term));
    const hasSkills = terms.some(term => skillKeywords.includes(term));
    
    if (hasTitle) priority = 'title';
    else if (hasEmployer) priority = 'employer';
    else if (hasSkills) priority = 'skills';
    else if (terms.some(term => ['education', 'course', 'certification', 'degree', 'diploma'].includes(term))) priority = 'courses';
    else if (terms.some(term => ['technology', 'healthcare', 'finance', 'marketing', 'sales', 'education', 'retail', 'government'].includes(term))) priority = 'category';
    
    return {
      keywords: searchTerm,
      searchPriority: priority,
      searchStrategy: 'prioritized' as const
    };
  };

  // Debounced search function
  const debouncedSearch = useCallback((searchTerm: string, locationTerm: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      const searchParams = parseSearchTerms(searchTerm);
      setFilters(prev => ({
        ...prev,
        ...searchParams,
        location: locationTerm
      }));
    }, 300); // 300ms delay
  }, []);

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value, locationInput);
  };

  const handleLocationChange = (value: string) => {
    setLocationInput(value);
    debouncedSearch(searchInput, value);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Reset pagination when filters change
    setPage(1);
    setJobs([]);
    setHasMore(false);
    fetchJobs(true);
  }, [filters]);

  useEffect(() => {
    if (currentUser) {
      fetchSavedJobs();
      fetchAppliedJobs();
    }
  }, [currentUser]);

  const fetchJobs = async (reset = false, pageNum = 1) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const params = new URLSearchParams();
      
      // Add pagination parameters - only if backend supports it
      if (pageNum > 1) {
        params.append('page', pageNum.toString());
        params.append('limit', '20');
      } else {
        // For first page, get more jobs to enable scrolling
        params.append('limit', '50');
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
      
      // Add search optimization flags (only for authenticated users)
      if (currentUser && filters.searchStrategy) {
        params.append('searchStrategy', filters.searchStrategy);
      }
      if (currentUser && filters.searchPriority) {
        params.append('searchPriority', filters.searchPriority);
      }

      const headers: Record<string, string> = {};
      
      // Only add Authorization header if user is authenticated
      if (currentUser && localStorage.getItem('authToken')) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('authToken')}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs?${params.toString()}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const result = await response.json();
      console.log('API Response:', { pageNum, reset, resultData: result.data });
      
      if (result.success) {
        const newJobs = result.data.jobs || result.data || []; // Handle different response structures
        const newMatches = result.data.matches || {};
        
        console.log('Processing jobs:', { newJobsCount: newJobs.length, reset, pageNum });
        
        if (reset) {
          setJobs(newJobs);
          setMatches(currentUser ? newMatches : {});
          setPage(1);
        } else {
          // Append new jobs, avoiding duplicates
          setJobs(prev => {
            const existingIds = new Set(prev.map(job => job.id));
            const filteredNewJobs = newJobs.filter((job: Job) => !existingIds.has(job.id));
            console.log('Appending jobs:', { existing: prev.length, new: filteredNewJobs.length });
            return [...prev, ...filteredNewJobs];
          });
          if (currentUser) {
            setMatches(prev => ({ ...prev, ...newMatches }));
          }
          setPage(pageNum);
        }
        
        // Update pagination state - be more flexible about hasMore detection
        const totalFromAPI = result.data.total || result.total;
        const currentJobCount = reset ? newJobs.length : jobs.length + newJobs.length;
        
        if (totalFromAPI) {
          setTotalJobs(totalFromAPI);
          setHasMore(currentJobCount < totalFromAPI);
        } else {
          // Fallback: assume more pages if we got a full page (20 jobs)
          setTotalJobs(prev => Math.max(prev, currentJobCount));
          setHasMore(newJobs.length >= 20);
        }
        
        console.log('Updated pagination:', { 
          totalFromAPI: totalFromAPI || 'unknown', 
          currentJobCount,
          newJobsLength: newJobs.length,
          hasMore: totalFromAPI ? currentJobCount < totalFromAPI : newJobs.length >= 20,
          pageNum
        });
      } else {
        toast.error(result.message || 'Failed to fetch jobs');
      }
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs. Refresh the page.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreJobs = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchJobs(false, page + 1);
    }
  }, [loadingMore, hasMore, page]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        console.log('Intersection observed:', { 
          isIntersecting: entry.isIntersecting, 
          hasMore, 
          loadingMore,
          currentPage: page
        });
        if (entry.isIntersecting && hasMore && !loadingMore) {
          console.log('Triggering load more jobs');
          loadMoreJobs();
        }
      },
      { threshold: 0.1, rootMargin: '100px' } // Added rootMargin for earlier trigger
    );

    const currentObserverRef = observerRef.current;
    if (currentObserverRef && hasMore) {
      console.log('Setting up observer');
      observer.observe(currentObserverRef);
    }

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [hasMore, loadingMore, loadMoreJobs, page]);

  const fetchSavedJobs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/saved`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSavedJobs(new Set(result.data.map((job: any) => job.id)));
        }
      }
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
    }
  };

  const fetchAppliedJobs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/applications/my`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAppliedJobs(new Set(result.data.applications.map((app: any) => app.jobId)));
        }
      }
    } catch (error) {
      console.error('Error fetching applied jobs:', error);
    }
  };

  const toggleSaveJob = async (jobId: string) => {
    try {
      const isSaved = savedJobs.has(jobId);
      const method = isSaved ? 'DELETE' : 'POST';
      const endpoint = isSaved ? `/jobs/${jobId}/unsave` : `/jobs/${jobId}/save`;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const newSavedJobs = new Set(savedJobs);
        if (isSaved) {
          newSavedJobs.delete(jobId);
          toast.success('Job removed from saved');
        } else {
          newSavedJobs.add(jobId);
          toast.success('Job saved successfully');
        }
        setSavedJobs(newSavedJobs);
      } else {
        toast.error('Failed to update saved job');
      }
    } catch (error) {
      console.error('Error toggling save job:', error);
      toast.error('Failed to update saved job');
    }
  };


  const clearFilters = () => {
    setFilters({ sortBy: 'match_score' });
  };

  const getActiveFilterCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === 'sortBy' || key === 'searchPriority' || key === 'searchStrategy') return false;
      return value !== undefined && value !== '' && 
             (!Array.isArray(value) || value.length > 0);
    }).length;
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Find Your Perfect Job</h1>
              <p className="text-gray-600 mt-2">
                Discover opportunities tailored to your skills and get AI-powered insights
              </p>
            </div>
            <div className="flex items-center gap-4">
              {currentUser ? (
                <>
                  <AIRecommendationButton />
                  <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2">
                    <BellIcon className="h-5 w-5" />
                    Job Alerts
                  </button>
                </>
              ) : (
                <div className="text-sm text-gray-600">
                  <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-medium">
                    Sign in
                  </Link>
                  {" "}for personalized job matches and alerts
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs, companies, or skills... (e.g., 'Senior React Developer', 'Google', 'JavaScript')"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {searchInput && filters.searchPriority && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    🎯 {filters.searchPriority === 'title' ? 'Title' : 
                         filters.searchPriority === 'employer' ? 'Company' :
                         filters.searchPriority === 'skills' ? 'Skills' :
                         filters.searchPriority === 'category' ? 'Category' : 'Courses'} priority
                  </span>
                </div>
              )}
            </div>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Location"
                value={locationInput}
                onChange={(e) => handleLocationChange(e.target.value)}
                className="w-64 pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 relative text-sm sm:text-base whitespace-nowrap"
            >
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Filters</span>
              <span className="sm:hidden">Filter</span>
              {getActiveFilterCount() > 0 && (
                <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-indigo-600 text-white text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center">
                  {getActiveFilterCount()}
                </span>
              )}
            </button>
          </div>

          {/* Active Filters */}
          {getActiveFilterCount() > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-sm text-gray-500">Active filters:</span>
              {Object.entries(filters).map(([key, value]) => {
                if (key === 'sortBy' || key === 'searchPriority' || key === 'searchStrategy' || !value || (Array.isArray(value) && value.length === 0)) return null;
                return (
                  <span
  key={key}
  className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
>
  {Array.isArray(value) ? value.join(', ') : value.toString()}
  <button
    onClick={() =>
      setFilters((prev) => ({ ...prev, [key]: undefined }))
    }
  >
    <XMarkIcon className="h-4 w-4" />
  </button>
</span>

                );
              })}
              <button onClick={clearFilters} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="lg:w-80 lg:flex-shrink-0">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 lg:sticky lg:top-8">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  <button onClick={() => setShowFilters(false)} className="lg:hidden">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Remote Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Type</label>
                    <div className="space-y-2">
                      {remoteTypeOptions.map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.remoteType?.includes(option.value) || false}
                            onChange={(e) => {
                              const current = filters.remoteType || [];
                              if (e.target.checked) {
                                setFilters(prev => ({ ...prev, remoteType: [...current, option.value] }));
                              } else {
                                setFilters(prev => ({ ...prev, remoteType: current.filter(v => v !== option.value) }));
                              }
                            }}
                            className="mr-2"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Job Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                    <div className="space-y-2">
                      {jobTypeOptions.map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.jobType?.includes(option.value) || false}
                            onChange={(e) => {
                              const current = filters.jobType || [];
                              if (e.target.checked) {
                                setFilters(prev => ({ ...prev, jobType: [...current, option.value] }));
                              } else {
                                setFilters(prev => ({ ...prev, jobType: current.filter(v => v !== option.value) }));
                              }
                            }}
                            className="mr-2"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Experience Level */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                    <div className="space-y-2">
                      {experienceLevelOptions.map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.experienceLevel?.includes(option.value) || false}
                            onChange={(e) => {
                              const current = filters.experienceLevel || [];
                              if (e.target.checked) {
                                setFilters(prev => ({ ...prev, experienceLevel: [...current, option.value] }));
                              } else {
                                setFilters(prev => ({ ...prev, experienceLevel: current.filter(v => v !== option.value) }));
                              }
                            }}
                            className="mr-2"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Salary Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Salary Range</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.salaryMin || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, salaryMin: e.target.value ? parseInt(e.target.value) : undefined }))}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.salaryMax || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, salaryMax: e.target.value ? parseInt(e.target.value) : undefined }))}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Match Score - Only for authenticated users */}
                  {currentUser && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Match Score</label>
                      <select
                        value={filters.minimumMatchScore || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, minimumMatchScore: e.target.value ? parseInt(e.target.value) : undefined }))}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Any match</option>
                        <option value="90">90%+ (Excellent match)</option>
                        <option value="75">75%+ (Good match)</option>
                        <option value="60">60%+ (Fair match)</option>
                        <option value="50">50%+ (Basic match)</option>
                      </select>
                    </div>
                  )}

                  {/* Posted Within */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Posted Within</label>
                    <select
                      value={filters.postedWithin || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, postedWithin: e.target.value ? parseInt(e.target.value) : undefined }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Any time</option>
                      {postedWithinOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {getActiveFilterCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    className="w-full mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Job Listings */}
          <div className="flex-1">
            {/* Sort and Results Count */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <p className="text-gray-600">
                  {loading ? 'Loading...' : (
                    totalJobs > 0 ? (
                      `Showing ${jobs.length} jobs${hasMore ? ' (scroll for more)' : ''}`
                    ) : (
                      `${jobs.length} jobs found`
                    )
                  )}
                </p>
                {currentUser && filters.searchStrategy === 'prioritized' && filters.keywords && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ⚡ Smart Search Active
                    </span>
                    <span className="text-xs text-gray-500">
                      Prioritizing {filters.searchPriority} matches
                    </span>
                  </div>
                )}
              </div>
              <select
                value={filters.sortBy || 'match_score'}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as any }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Job Cards */}
            <div className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No jobs found matching your criteria.</p>
                  <button onClick={clearFilters} className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium">
                    Clear filters to see all jobs
                  </button>
                </div>
              ) : (
                <>
                  {jobs.map(job => {
                    const match = currentUser ? matches[job.id] : undefined;
                    return (
                      <JobCard 
                        key={job.id} 
                        job={job} 
                        match={match}
                        appliedJobs={appliedJobs}
                        onApply={(job) => {
                          if (currentUser) {
                            setSelectedJob(job);
                            setShowApplicationModal(true);
                          }
                        }}
                        onToggleSave={currentUser ? () => toggleSaveJob(job.id) : undefined}
                        isSaved={currentUser ? savedJobs.has(job.id) : false}
                        isAuthenticated={!!currentUser}
                      />
                    );
                  })}
                  
                  {/* Infinite scroll trigger and manual fallback */}
                  <div ref={observerRef} className="h-20 flex flex-col justify-center items-center gap-2">
                    {loadingMore ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                        Loading more jobs...
                      </div>
                    ) : hasMore ? (
                      <button
                        onClick={loadMoreJobs}
                        className="px-4 py-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium transition-colors"
                      >
                        ↓ Load More Jobs (or scroll to auto-load)
                      </button>
                    ) : null}
                    
                    {/* Debug info */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="text-xs text-gray-400 text-center">
                        <div>Page: {page} | Searching </div>
                      </div>
                    )}
                  </div>
                  
                  {/* End of results message */}
                  {!hasMore && jobs.length > 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">You've reached the end of the job listings.</p>
                      {filters.searchStrategy === 'prioritized' && (
                        <p className="text-sm text-gray-400 mt-2">
                          💡 Try different search terms or adjust filters to find more jobs
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {selectedJob && (
        <JobApplicationModal
          job={{
            ...selectedJob,
            skills: selectedJob.skillsRequired || [], // Adapt skillsRequired to skills for modal
            salaryRange: selectedJob.salaryRange || { min: 0, max: 0, currency: 'USD' } // Provide default salary range
          }}
          isOpen={showApplicationModal}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedJob(null);
          }}
          onSuccess={() => {
            setShowApplicationModal(false);
            if (selectedJob) {
              setAppliedJobs(prev => new Set(Array.from(prev).concat(selectedJob.id)));
            }
            setSelectedJob(null);
            // Refresh jobs data to get updated application counts
            fetchJobs();
            // Refresh applied jobs to ensure persistence across page reloads
            fetchAppliedJobs();
            toast.success('Application submitted successfully!');
          }}
        />
      )}
    </div>
  );
};

// Job Card Component
const JobCard: React.FC<{ 
  job: Job; 
  match?: JobMatch; 
  onApply: (job: Job) => void; 
  appliedJobs?: Set<string>;
  onToggleSave?: () => void;
  isSaved?: boolean;
  isAuthenticated?: boolean;
}> = ({ job, match, onApply, appliedJobs, onToggleSave, isSaved, isAuthenticated = false }) => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const formatSalary = (min: number, max: number, currency: string = 'USD') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    });
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 75) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getQualificationColor = (level: string) => {
    switch (level) {
      case 'over-qualified':
        return 'bg-purple-100 text-purple-800';
      case 'well-qualified':
        return 'bg-green-100 text-green-800';
      case 'qualified':
        return 'bg-blue-100 text-blue-800';
      case 'under-qualified':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeAgo = (dateInput: string | { _seconds: number; _nanoseconds: number }) => {
    let date: Date;
    
    // Handle Firebase timestamp format
    if (typeof dateInput === 'object' && dateInput._seconds) {
      date = new Date(dateInput._seconds * 1000);
    } else if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else {
      return 'Unknown date';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown date';
    }
    
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just posted';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  // Check for auto-offer status
  const isAutoOffer = job.autoOffer?.enabled || job.autoOfferEnabled;
  const autoOfferDeadline = job.autoOfferDeadline || job.autoOffer?.createdAt;
  const timeRemaining = isAutoOffer && autoOfferDeadline ? (() => {
    let deadline: Date;
    
    // Handle Firebase timestamp format for autoOfferDeadline
    if (typeof autoOfferDeadline === 'object' && autoOfferDeadline && 'seconds' in autoOfferDeadline) {
      deadline = new Date((autoOfferDeadline as any).seconds * 1000);
    } else if (typeof autoOfferDeadline === 'object' && autoOfferDeadline && '_seconds' in autoOfferDeadline) {
      deadline = new Date((autoOfferDeadline as any)._seconds * 1000);
    } else if (typeof autoOfferDeadline === 'object' && autoOfferDeadline && 'toDate' in autoOfferDeadline) {
      deadline = (autoOfferDeadline as any).toDate();
    } else {
      deadline = new Date(autoOfferDeadline as string | Date);
    }
    
    // Check if deadline is valid
    if (isNaN(deadline.getTime())) {
      return null;
    }
    
    // For JobsPage, we need to calculate the actual deadline if autoOfferDeadline is the direct deadline
    // If it's job.autoOffer?.createdAt, we need to add the timeLimit
    let actualDeadline: Date;
    if (job.autoOfferDeadline) {
      // autoOfferDeadline is the actual deadline
      actualDeadline = deadline;
    } else {
      // autoOfferDeadline is createdAt, so add timeLimit
      const timeLimit = job.autoOffer?.timeLimit || job.autoOfferTimeLimit || 72;
      if (isNaN(timeLimit) || timeLimit <= 0) {
        return null;
      }
      actualDeadline = new Date(deadline.getTime() + (timeLimit * 60 * 60 * 1000));
    }
    
    const now = new Date();
    const timeDiff = actualDeadline.getTime() - now.getTime();
    
    // Check if timeDiff calculation is valid
    if (isNaN(timeDiff)) {
      return null;
    }
    
    const hoursLeft = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60)));
    const minutesLeft = Math.max(0, Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60)));
    
    if (timeDiff <= 0) return 'Ended';
    if (hoursLeft > 24) {
      const days = Math.floor(hoursLeft / 24);
      return `${days}d ${hoursLeft % 24}h left`;
    } else if (hoursLeft > 0) {
      return `${hoursLeft}h ${minutesLeft}m left`;
    } else {
      return `${minutesLeft}m left`;
    }
  })() : null;

  return (
    <div className={`rounded-lg p-4 sm:p-6 transition-all duration-300 ${isAutoOffer ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 shadow-lg hover:shadow-xl ring-1 ring-green-200 relative' : 'bg-white shadow-sm border border-gray-200 hover:shadow-md'}`}>
      {/* Auto-offer floating badge */}
      {isAutoOffer && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold shadow-lg border border-green-400 animate-pulse">
            <span className="hidden sm:inline">⚡ AUTO-HIRE</span>
            <span className="sm:hidden">⚡ AUTO</span>
          </div>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-3">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
            <h3 className={`text-lg sm:text-xl font-semibold ${isAutoOffer ? 'text-green-900' : 'text-gray-900'} break-words`}>
              <Link to={`/jobs/${job.id}`} className={`${isAutoOffer ? 'hover:text-green-700' : 'hover:text-indigo-600'}`}>
                {job.title}
              </Link>
            </h3>
            {isAutoOffer && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 sm:px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full text-xs font-bold shadow-md border border-green-400 flex items-center gap-1 sm:gap-1.5">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden sm:inline">🚀 AUTO-OFFER</span>
                  <span className="sm:hidden">🚀 AUTO</span>
                </span>
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs font-medium border border-yellow-200 whitespace-nowrap">
                  FAST TRACK
                </span>
              </div>
            )}
            {job.isFeatured && !isAutoOffer && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                Featured
              </span>
            )}
          </div>
          <div className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm mb-3 ${isAutoOffer ? 'text-green-700' : 'text-gray-600'}`}>
            <div className="flex items-center gap-1">
              <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{job.employerName}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPinIcon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{job.location} • {job.remoteType}</span>
            </div>
            <div className="flex items-center gap-1">
              <ClockIcon className="h-4 w-4 flex-shrink-0" />
              {getTimeAgo(job.createdAt)}
            </div>
            {isAutoOffer && timeRemaining && (
              <div className="flex items-center gap-1 font-bold text-green-700">
                ⏰ {timeRemaining}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 self-start sm:self-center">
          <button 
            onClick={isAuthenticated ? onToggleSave : () => setShowLoginPrompt(true)}
            className={`p-2 hover:text-gray-600 ${isSaved ? 'text-indigo-600' : 'text-gray-400'} self-start`}
          >
            <BookmarkIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${isSaved ? 'fill-current' : ''}`} />
          </button>
          {match && (
            <div className={`px-2 sm:px-3 py-1 rounded-lg border text-xs sm:text-sm font-medium ${getMatchScoreColor(match.overallMatchScore)} whitespace-nowrap`}>
              {match.overallMatchScore}% match
            </div>
          )}
        </div>
      </div>

      {/* Job Details */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
          {job.jobType}
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
          {job.experienceLevel} level
        </span>
        <span className={`px-3 py-1 text-sm rounded-full ${getCompetitionColor(job.competitionLevel)}`}>
          {job.competitionLevel} competition
        </span>
        {match && (
          <span className={`px-3 py-1 text-sm rounded-full ${getQualificationColor(match.qualificationLevel)}`}>
            {match.qualificationLevel.replace('-', ' ')}
          </span>
        )}
      </div>

      {/* Job Description */}
      <div className="text-gray-600 mb-4 line-clamp-2">
        <p>
          {job.description?.includes('<') && job.description?.includes('>') ? (
            // For HTML content, strip tags and truncate for job listing
            cleanHTMLToText(job.description, 150)
          ) : (
            // For plain text, display normally with truncation
            job.description && job.description.length > 150 
              ? job.description.substring(0, 150) + '...'
              : job.description
          )}
        </p>
      </div>

      {/* Skills & Match Analysis */}
      {match && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {match.matchedSkills.slice(0, 5).map(skill => (
              <span key={skill} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md border border-green-200">
                ✓ {skill}
              </span>
            ))}
            {match.missingSkills.slice(0, 3).map(skill => (
              <span key={skill} className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded-md border border-red-200">
                ✗ {skill}
              </span>
            ))}
            {match.missingSkills.length > 3 && (
              <span className="px-2 py-1 bg-gray-50 text-gray-500 text-xs rounded-md">
                +{match.missingSkills.length - 3} more
              </span>
            )}
          </div>

          {match.improvementSuggestions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-blue-800 text-sm font-medium mb-1">💡 Improvement Tip:</p>
              <p className="text-blue-700 text-sm">{match.improvementSuggestions[0]}</p>
            </div>
          )}
        </div>
      )}

      {/* Auto-offer Information */}
      {isAutoOffer && (
        <div className="mb-4 p-3 sm:p-4 bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 rounded-lg shadow-sm">
          <div className="space-y-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">⚡</span>
                <h4 className="text-sm font-bold text-green-800">
                  Auto-Offer Active - Apply Fast!
                </h4>
              </div>
              <p className="text-xs text-green-700 mb-3 font-medium">
                🎯 Top qualifying candidate gets automatically offered the position when timer expires
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                {((job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses) && (job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses)!.length > 0) && (
                  <div className="text-xs">
                    <span className="font-bold text-green-800 flex items-center gap-1 mb-1">
                      📚 Required Courses:
                    </span>
                    <div className="space-y-1">
                      {(job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses)!.slice(0, 3).map((course, idx) => (
                        <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white bg-opacity-60 px-2 py-1 rounded text-xs gap-1 sm:gap-0">
                          <span className="font-medium text-green-800 break-words">{course.courseName}</span>
                          <span className="text-green-600 font-bold self-start sm:self-center">{course.minimumGrade}%+</span>
                        </div>
                      ))}
                      {(job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses)!.length > 3 && (
                        <div className="text-xs text-green-600 font-medium">
                          +{(job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses)!.length - 3} more courses
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  {/* {(job.autoOffer?.minimumOverallScore || job.autoOfferMinimumScore) && (
                    <div className="flex items-center justify-between bg-white bg-opacity-60 px-2 py-1 rounded text-xs">
                      <span className="font-bold text-green-800">⭐ Min Score:</span> 
                      <span className="text-green-600 font-bold">{job.autoOffer?.minimumOverallScore || job.autoOfferMinimumScore}%</span> 
                    </div>
                  )} */}
                  {isAuthenticated && match && (
                    <div className={`flex items-center justify-between px-2 py-1 rounded text-xs font-bold ${
                      match.overallMatchScore >= (job.autoOffer?.minimumOverallScore || job.autoOfferMinimumScore || 80)
                        ? 'bg-green-200 text-green-800'
                        : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      <span>🎯 Your Score:</span>
                      <span>{match.overallMatchScore}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {job.salaryRange && (
              <div className="flex items-center gap-1">
                <CurrencyDollarIcon className="h-4 w-4" />
                {formatSalary(job.salaryRange.min, job.salaryRange.max, job.salaryRange.currency)}
              </div>
            )}
            <div className="flex items-center gap-1">
              <UsersIcon className="h-4 w-4" />
              {/* The API omits applicationsCount entirely when signed out, so
                  there is no real number here to reveal. */}
              {job.applicationsCountLocked ? (
                <RedactedValue bars={2} label="applicants" />
              ) : (
                `${job.applicationsCount || 0} applicants`
              )}
            </div>
            <div className="flex items-center gap-1">
              <EyeIcon className="h-4 w-4" />
              {job.viewsCount || 0} views
            </div>
            {job.applicationDeadline && (
              <div className="flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" />
                Deadline: {new Date(job.applicationDeadline).toLocaleDateString()}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Link
              to={`/jobs/${job.id}`}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border transition-colors text-center ${
                isAutoOffer 
                  ? 'text-green-700 border-green-600 hover:bg-green-50'
                  : 'text-indigo-600 border-indigo-600 hover:bg-indigo-50'
              }`}
            >
              <span className="hidden sm:inline">View Details</span>
              <span className="sm:hidden">Details</span>
            </Link>
            {isAuthenticated ? (
              appliedJobs?.has(job.id) ? (
                <button
                  disabled
                  className="px-3 sm:px-4 py-2 bg-green-100 text-green-800 rounded-md text-xs sm:text-sm font-medium cursor-not-allowed flex items-center justify-center gap-1 sm:gap-2"
                >
                  <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  Applied
                </button>
              ) : (
                <button
                  onClick={() => onApply(job)}
                  className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 text-center ${
                    isAutoOffer 
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg transform hover:scale-105'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {isAutoOffer ? (
                    <>
                      <span className="hidden sm:inline">🚀 Apply Fast!</span>
                      <span className="sm:hidden">🚀 Apply</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Apply Now</span>
                      <span className="sm:hidden">Apply</span>
                    </>
                  )}
                </button>
              )
            ) : (
              <button
                onClick={() => setShowLoginPrompt(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  isAutoOffer 
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg transform hover:scale-105'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {isAutoOffer ? '🚀 Apply Fast!' : 'Apply Now'}
              </button>
            )}
          </div>
        </div>

        {/* Qualifying Courses for non-logged users */}
        {!isAuthenticated && job.skillsRequired && job.skillsRequired.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <AcademicCapIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-gray-600">qualifying courses</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {job.skillsRequired.slice(0, 4).map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  {skill}
                </span>
              ))}
              {job.skillsRequired.length > 4 && (
                <Link
                  to="/courses"
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  +{job.skillsRequired.length - 4} more
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
                <CheckCircleIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sign in to Apply</h3>
              <p className="text-sm text-gray-500 mb-6">
                Create an account or sign in to apply for jobs and track your applications.
              </p>
              <div className="flex gap-3">
                <Link
                  to="/register"
                  className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 text-sm font-medium text-center"
                >
                  Sign Up Free
                </Link>
                <Link
                  to="/login"
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 text-sm font-medium text-center"
                >
                  Log In
                </Link>
              </div>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="mt-3 text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// AI Recommendation Button Component (Paid Plan Only)
const AIRecommendationButton: React.FC = () => {
  const { currentUser } = useAuth();
  const [userPlan, setUserPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      fetchUserPlan();
    }
  }, [currentUser]);

  const fetchUserPlan = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/subscriptions/my-plan`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserPlan(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendationClick = () => {
    // Check if user has paid plan
    const hasPaidAccess = userPlan && userPlan.tier !== 'free';
    
    if (!hasPaidAccess) {
      toast.error('AI Recommendations are available for Plus and Pro subscribers only!');
      // Could redirect to subscription page
      window.location.href = '/subscription';
      return;
    }

    // Navigate to AI recommendations
    window.location.href = '/jobs/ai-recommendations';
  };

  if (loading) {
    return (
      <button disabled className="bg-gray-200 text-gray-500 px-4 py-2 rounded-lg flex items-center gap-2">
        <SparklesIcon className="h-5 w-5" />
        Optimize
      </button>
    );
  }

  const hasPaidAccess = userPlan && userPlan.tier !== 'free';

  return (
    <button
      onClick={handleRecommendationClick}
      className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
        hasPaidAccess
          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-dashed border-purple-300'
      }`}
    >
      <SparklesIcon className="h-5 w-5" />
      Optimize
      {!hasPaidAccess && (
        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full ml-1">
          Pro
        </span>
      )}
    </button>
  );
};

export default JobsPage;